import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Tab } from '@material-ui/core';
import { TabPanel, TabList, TabContext } from '@material-ui/lab';

import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import { GQLMerchantChannelCollection, GQLParty } from '@jewelutions/core-crm-server-api-client-domain';
import { JwlTable, LoadingSpinner } from '../../../../components';
import { GQLPieceConfigurationHelper, GQLPieceHelper } from '../../../../operations/helpers/GQLPieceHelper';
import {
  GQLAccessoryConfigurationHelper,
  GQLAccessoryHelper
} from '../../../../operations/helpers/GQLAccessoryHelper';

interface IProps {
  party: GQLParty;
  merchantChannelCollection: GQLMerchantChannelCollection;
}

const MerchantChannelCollectionAvailability: React.FC<IProps> = ({
  party,
  merchantChannelCollection
}: IProps) => {
  const [tabValue, setTabValue] = useState('pieces');

  const { t: translate } = useTranslation();

  const { loading: fontsLoading, data: fontsResponse } = GQLOperationBuilder.font.getFonts({
    fontIds: merchantChannelCollection.availableFontIds
  });

  const { loading: piecesLoading, data: piecesResponse } = GQLOperationBuilder.piece.getPieces({
    pieceIds: merchantChannelCollection.availablePieceIds
  });

  const {
    loading: accessoriesLoading,
    data: accessoriesResponse
  } = GQLOperationBuilder.accessory.getAccessories({
    accessoryIds: merchantChannelCollection.availableAccessoryIds
  });

  const {
    loading: symbolCollectionsLoading,
    data: symbolCollectionResponse
  } = GQLOperationBuilder.symbolCollection.getSymbolCollections({
    symbolCollectionIds: merchantChannelCollection.availableSymbolCollectionIds
  });

  if (piecesLoading || accessoriesLoading || fontsLoading || symbolCollectionsLoading)
    return <LoadingSpinner />;
  const handleTabChange = (event: any, newValue: string) => {
    setTabValue(newValue);
  };

  const pieces = GQLOperationBuilder.piece.pieceAssetArrayFromResponse(piecesResponse);
  const accessories = GQLOperationBuilder.accessory.accessoryAssetArrayFromResponse(accessoriesResponse);
  const fonts = GQLOperationBuilder.font.fontAssetArrayFromResponse(fontsResponse);
  const symbolCollections = GQLOperationBuilder.symbolCollection.symbolCollectionArrayFromResponse(
    symbolCollectionResponse
  );

  const getPiecesRows = () => {
    return pieces.map(piece => {
      const pieceAvailableConfigurations = GQLPieceHelper.getAllAvailablePieceConfigurations(piece.piece);
      return {
        id: piece.id,
        cells: [
          piece.piece.title.en,
          piece.piece.geometryType,
          GQLPieceConfigurationHelper.getAvailableSizes(pieceAvailableConfigurations).size,
          GQLPieceConfigurationHelper.getAvailableStonePlacementTypes(pieceAvailableConfigurations).size,
          GQLPieceConfigurationHelper.getAvailablePlatingTypes(pieceAvailableConfigurations).size
        ]
      };
    });
  };

  const getAccessoriesRows = () => {
    return accessories.map(accessory => {
      const accessoryavAilableConfigurations = GQLAccessoryHelper.getAllAvailableAccessoryConfigurations(
        accessory.accessory
      );
      return {
        id: accessory.id,
        cells: [
          accessory.accessory.title.en,
          accessory.accessory.type,
          GQLAccessoryConfigurationHelper.getAvailableSizes(accessoryavAilableConfigurations).size,
          GQLAccessoryConfigurationHelper.getAvailablePlatingTypes(accessoryavAilableConfigurations).size
        ]
      };
    });
  };

  const getFontsRows = () => {
    return fonts.map(font => {
      return {
        id: font.id,
        cells: [font.font.name]
      };
    });
  };

  const getSymbolCollectionsRows = () => {
    return symbolCollections.map(symbolCollection => {
      return {
        id: symbolCollection.id,
        cells: [symbolCollection.title.en, symbolCollection.description.en, symbolCollection.symbols.length]
      };
    });
  };

  return (
    <Grid item style={{ marginTop: '8px' }}>
      <TabContext value={tabValue}>
        <TabList onChange={(event, tabIndex) => handleTabChange(event, tabIndex)}>
          <Tab label={`Collection pieces`} value={'pieces'} />
          <Tab label={`Collection accessories`} value={'accessories'} />
          <Tab label={`Collection fonts`} value={'fonts'} />
          <Tab label={`Collection symbol collections`} value={'symbols'} />
        </TabList>
        <TabPanel value={'pieces'}>
          <JwlTable
            title="Pieces"
            description="If you have been reading the overrides documentation page but you are, add more metadata..."
            columns={['Piece name', 'Geometry', 'Sizes', 'Stone placements', 'Platings']}
            rows={getPiecesRows()}
            height="400px"
            size="small"
          />
        </TabPanel>
        <TabPanel value={'accessories'}>
          <JwlTable
            title="Accessories"
            description="If you have been reading the overrides documentation page but you are, add more metadata..."
            columns={['Accessory name', 'Type', 'Sizes', 'Platings']}
            rows={getAccessoriesRows()}
            height="400px"
            size="small"
          />
        </TabPanel>
        <TabPanel value={'fonts'}>
          <JwlTable
            title="Fonts"
            description="If you have been reading the overrides documentation page but you are, add more metadata..."
            columns={['Font name']}
            rows={getFontsRows()}
            height="400px"
            size="small"
          />
        </TabPanel>
        <TabPanel value={'symbols'}>
          <JwlTable
            title="Symbol collections"
            description="If you have been reading the overrides documentation page but you are, add more metadata..."
            columns={[
              'Symbol collection name',
              'Symbol collection description',
              'Number of containing symbols'
            ]}
            rows={getSymbolCollectionsRows()}
            height="400px"
            size="small"
          />
        </TabPanel>
      </TabContext>
    </Grid>
  );
};

export default MerchantChannelCollectionAvailability;
